import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { AssistantConfirmCard } from '@/components/assistant/AssistantConfirmCard'
import { AssistantCardPathContext } from '@/components/assistant/AssistantNavigation'
import {
  dailyLimitNotice,
  isDailyLimitActive,
} from '@/components/assistant/daily-limit'
import {
  getErrorPresentation,
  isErrorMessage,
} from '@/components/assistant/error-presentation'
import { sanitizeStreamingContent } from '@/components/assistant/message-content'
import {
  assistantUrlTransform,
  ModuleCardList,
  normalizeAssistantCardsForContext,
  parseScramblCards,
  parseScramblFollowups,
} from '@/components/assistant/ModuleCards'
import { toolCallLabel } from '@/components/assistant/tool-labels'
import { useConversationScroll } from '@/components/assistant/useConversationScroll'
import {
  clientPortalAssistantKeys,
  clientPortalAssistantMessagesOptions,
  useClientPortalAssistantCapabilities,
  useClientPortalAssistantMessages,
  useClientPortalAssistantOwner,
} from '@/hooks/useClientPortalAssistant'
import { clientPortalAssistantApi } from '@/lib/api/client-portal-assistant.api'
import { getPortalAssistantCardPath } from '@/lib/client-portal/portal-card-routes'
import { cn } from '@/lib/utils'
import {
  ArrowsOutSimple,
  ArrowUp,
  CheckCircle,
  CircleNotch,
  Plus,
  WarningCircle,
  X,
  XCircle,
} from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import { Link } from 'react-router-dom'
import remarkGfm from 'remark-gfm'

import type {
  AssistantChatMessage,
  AssistantChatStreamEvent,
  AssistantConfirmation,
} from '@scrambl/shared'

const remarkPlugins = [remarkGfm]

interface InFlightTurn {
  /** What the user just sent; empty for a confirmation answer. */
  userText: string
  /** The streaming reply for the current model message. */
  content: string
  tools: {
    id: string
    name: string
    args: unknown
    status: 'working' | 'completed' | 'failed'
  }[]
}

interface StreamHandlers {
  signal: AbortSignal
  onEvent: (event: AssistantChatStreamEvent) => void
}

export interface PortalBennyChatProps {
  entityId: string
  /** The open conversation; undefined starts a new one on the next send. */
  sessionId: string | undefined
  onSessionChange: (sessionId: string | undefined) => void
  variant: 'popover' | 'page'
  /** Internal staff previewing the portal can read but never write. */
  readOnly?: boolean
  onExpand?: () => void
  onClose?: () => void
}

function nodeText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(nodeText).join('')
  if (node && typeof node === 'object' && 'props' in node)
    return nodeText(
      (node as { props: { children?: ReactNode } }).props.children,
    )
  return ''
}

function isVisibleMessage(message: AssistantChatMessage) {
  if (message.role === 'user') return !!message.content?.trim()
  if (message.role !== 'assistant') return false
  return (
    !!message.content?.trim() ||
    !!message.metadata?.confirmation ||
    isErrorMessage(message)
  )
}

/**
 * Benny inside the client portal: one thread, owned by the signed-in client
 * user. Used in the top-bar popover and on the full Benny page.
 *
 * The server scopes every conversation to this user, so nothing here can
 * reach another person's thread. Record cards resolve through the portal's
 * own routes, never the org app's.
 */
export function PortalBennyChat({
  entityId,
  sessionId,
  onSessionChange,
  variant,
  readOnly = false,
  onExpand,
  onClose,
}: PortalBennyChatProps) {
  const queryClient = useQueryClient()
  const userId = useClientPortalAssistantOwner()
  const capabilities = useClientPortalAssistantCapabilities(entityId)
  const messagesQuery = useClientPortalAssistantMessages(entityId, sessionId)
  const [draft, setDraft] = useState('')
  const [turn, setTurn] = useState<InFlightTurn | null>(null)
  const [answeringId, setAnsweringId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const messages = [...(messagesQuery.data?.pages ?? [])]
    .reverse()
    .flatMap(page => page.items)
    .filter(isVisibleMessage)
  const lastAssistantId = [...messages]
    .reverse()
    .find(message => message.role === 'assistant')?.id
  const busy = turn !== null
  // A remembered conversation that was deleted (or never belonged to this
  // user) 404s; start fresh instead of showing a broken thread.
  const threadMissing = !!sessionId && messagesQuery.isError
  useEffect(() => {
    if (threadMissing && !busy) onSessionChange(undefined)
  }, [threadMissing, busy, onSessionChange])
  const { scrollRef, followLatest } = useConversationScroll(
    undefined,
    messages,
    turn?.content ?? null,
    turn?.tools.length ?? 0,
  )

  /** Stream a turn, then wait for the persisted reply before dropping the bubble. */
  const runTurn = async (
    start: (handlers: StreamHandlers) => Promise<void>,
    userText: string,
  ) => {
    let activeSessionId = sessionId
    const controller = new AbortController()
    const handleEvent = (event: AssistantChatStreamEvent) => {
      switch (event.type) {
        case 'session':
          activeSessionId = event.sessionId
          if (event.sessionId !== sessionId) onSessionChange(event.sessionId)
          break
        case 'message_start':
          setTurn(current => current && { ...current, content: '' })
          break
        case 'content_delta':
          setTurn(
            current =>
              current && { ...current, content: current.content + event.delta },
          )
          break
        case 'tool_call_start':
          setTurn(current =>
            !current || current.tools.some(tool => tool.id === event.toolCallId)
              ? current
              : {
                  ...current,
                  tools: [
                    ...current.tools,
                    {
                      id: event.toolCallId,
                      name: event.toolName,
                      args: event.args,
                      status: 'working',
                    },
                  ],
                },
          )
          break
        case 'tool_call_end':
          setTurn(
            current =>
              current && {
                ...current,
                tools: current.tools.map(tool =>
                  tool.id === event.toolCallId
                    ? { ...tool, status: event.status }
                    : tool,
                ),
              },
          )
          break
        case 'error':
          setNotice(
            event.resetsAt && isDailyLimitActive(event.resetsAt)
              ? dailyLimitNotice(event.resetsAt)
              : event.message || 'Benny couldn’t answer that. Try again.',
          )
          break
      }
    }

    setNotice(null)
    setTurn({ userText, content: '', tools: [] })
    followLatest()
    try {
      await start({ signal: controller.signal, onEvent: handleEvent })
    } catch {
      setNotice('Benny couldn’t answer that. Try again.')
    } finally {
      if (userId) {
        await queryClient.invalidateQueries({
          queryKey: clientPortalAssistantKeys.sessions(entityId, userId),
        })
        if (activeSessionId) {
          const options = clientPortalAssistantMessagesOptions(
            entityId,
            userId,
            activeSessionId,
          )
          await queryClient.invalidateQueries({ queryKey: options.queryKey })
          await queryClient.fetchInfiniteQuery(options).catch(() => undefined)
        }
      }
      setTurn(null)
    }
  }

  const send = (text: string) => {
    const message = text.trim()
    if (!message || busy || readOnly) return
    setDraft('')
    void runTurn(
      handlers =>
        clientPortalAssistantApi.chatStream(
          entityId,
          { message, sessionId },
          handlers,
        ),
      message,
    )
  }

  const answer = (
    messageId: string,
    confirmation: AssistantConfirmation,
    approve: boolean,
  ) => {
    if (!sessionId || busy || readOnly) return
    setAnsweringId(messageId)
    void runTurn(
      handlers =>
        clientPortalAssistantApi.confirmToolCalls(
          entityId,
          sessionId,
          {
            toolCallIds: confirmation.items.map(item => item.toolCallId),
            approve,
          },
          handlers,
        ),
      '',
    ).finally(() => setAnsweringId(null))
  }

  const startNewConversation = () => {
    if (busy) return
    setNotice(null)
    onSessionChange(undefined)
    inputRef.current?.focus()
  }

  const renderFence = (
    className: string | undefined,
    text: string,
    message: {
      id: string
      content: string
      showFollowups: boolean
    },
  ) => {
    if (className?.includes('language-scrambl-cards')) {
      const cards = parseScramblCards(text)
      return cards && cards.length > 0 ? (
        <ModuleCardList
          cards={normalizeAssistantCardsForContext(cards, message.content)}
          animationKey={message.id}
        />
      ) : null
    }
    if (className?.includes('language-scrambl-followups')) {
      const prompts = parseScramblFollowups(text)
      if (!prompts || !message.showFollowups || readOnly) return null
      return (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {prompts.map(prompt => (
            <button
              key={prompt}
              type="button"
              disabled={busy}
              onClick={() => send(prompt)}
              className="rounded-full border border-border/60 bg-background px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground disabled:opacity-50"
            >
              {prompt}
            </button>
          ))}
        </div>
      )
    }
    if (
      className?.includes('language-scrambl-canvas') ||
      className?.includes('language-scrambl-view')
    )
      return null
    return undefined
  }

  const renderMarkdown = (
    content: string,
    message: { id: string; showFollowups: boolean },
  ) => (
    <div className="prose prose-sm dark:prose-invert prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 max-w-none break-words text-sm leading-relaxed text-foreground">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        urlTransform={assistantUrlTransform}
        components={{
          a({ href, children }) {
            if (!href) return <>{children}</>
            if (href.startsWith('/'))
              return (
                <Link
                  to={href}
                  className="text-primary underline underline-offset-2"
                >
                  {children}
                </Link>
              )
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                {children}
              </a>
            )
          },
          pre({ children }) {
            const child = Array.isArray(children) ? children[0] : children
            if (child && typeof child === 'object' && 'props' in child) {
              const props = (
                child as {
                  props: { className?: string; children?: ReactNode }
                }
              ).props
              const rendered = renderFence(
                props.className,
                nodeText(props.children),
                { ...message, content },
              )
              if (rendered !== undefined) return rendered
            }
            return (
              <pre className="overflow-x-auto rounded-lg bg-muted p-3 text-xs">
                {children}
              </pre>
            )
          },
          code({ className, children }) {
            const rendered = renderFence(className, nodeText(children), {
              ...message,
              content,
            })
            if (rendered !== undefined) return rendered
            return <code className={className}>{children}</code>
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )

  const suggestedAsks = capabilities.data?.suggestedAsks ?? []
  const hasThread = messages.length > 0 || turn !== null
  const isLoadingThread = !!sessionId && messagesQuery.isLoading

  return (
    <AssistantCardPathContext
      value={card => getPortalAssistantCardPath(entityId, card)}
    >
      <div className="flex h-full min-h-0 flex-col bg-background">
        {variant === 'popover' && (
          <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
            <span className="flex items-center gap-1.5 pl-1 text-[13px] font-semibold tracking-tight">
              <span
                aria-hidden
                className="from-sunny-yolk h-2 w-2 rounded-full bg-gradient-to-br via-primary to-cyan-400 shadow-[0_0_8px_hsl(var(--primary)/0.5)]"
              />
              Benny
            </span>
            <div className="flex items-center gap-0.5">
              {!readOnly && (
                <button
                  type="button"
                  onClick={startNewConversation}
                  disabled={busy}
                  className="flex h-8 items-center gap-1 rounded-full px-2.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  New chat
                </button>
              )}
              {onExpand && (
                <button
                  type="button"
                  onClick={onExpand}
                  aria-label="Open Benny"
                  title="Open Benny"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <ArrowsOutSimple className="h-4 w-4" />
                </button>
              )}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close Benny"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </header>
        )}

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          <div
            className={cn(
              'flex flex-col gap-4 px-4 py-4',
              variant === 'page' && 'mx-auto w-full max-w-3xl py-6',
            )}
          >
            {messagesQuery.hasNextPage && (
              <button
                type="button"
                onClick={() => void messagesQuery.fetchNextPage()}
                disabled={messagesQuery.isFetchingNextPage}
                className="self-center rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {messagesQuery.isFetchingNextPage
                  ? 'Loading…'
                  : 'Load earlier messages'}
              </button>
            )}

            {isLoadingThread && (
              <div className="flex justify-center py-10 text-muted-foreground">
                <CircleNotch className="h-5 w-5 animate-spin" />
              </div>
            )}

            {!hasThread && !isLoadingThread && (
              <div className="flex flex-col items-start gap-3 py-6">
                <h2 className="text-xl font-semibold tracking-tight">
                  How can I help?
                </h2>
                <p className="text-sm text-muted-foreground">
                  Ask about your projects, requests and invoices. Your
                  conversations with Benny are only visible to you.
                </p>
                {!readOnly && suggestedAsks.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {suggestedAsks.map(ask => (
                      <button
                        key={ask}
                        type="button"
                        onClick={() => send(ask)}
                        className="rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
                      >
                        {ask}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {messages.map(message =>
              message.role === 'user' ? (
                <div key={message.id} className="flex justify-end">
                  <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary/10 px-3.5 py-2 text-sm text-foreground">
                    {message.content}
                  </div>
                </div>
              ) : (
                <div key={message.id} className="flex flex-col gap-2">
                  {isErrorMessage(message) && (
                    <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                      <WarningCircle className="h-4 w-4" />
                      {getErrorPresentation(message).title}
                    </div>
                  )}
                  {message.content?.trim() &&
                    renderMarkdown(message.content, {
                      id: message.id,
                      showFollowups: message.id === lastAssistantId && !busy,
                    })}
                  {message.metadata?.confirmation && (
                    <AssistantConfirmCard
                      confirmation={message.metadata.confirmation}
                      busy={answeringId === message.id}
                      onDecision={
                        !readOnly &&
                        message.metadata.confirmation.status === 'awaiting'
                          ? approve =>
                              answer(
                                message.id,
                                message.metadata!.confirmation!,
                                approve,
                              )
                          : undefined
                      }
                    />
                  )}
                </div>
              ),
            )}

            {turn && (
              <>
                {turn.userText && (
                  <div className="flex justify-end">
                    <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary/10 px-3.5 py-2 text-sm text-foreground">
                      {turn.userText}
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-2" aria-live="polite">
                  {turn.tools.length > 0 && (
                    <ul className="flex flex-col gap-1">
                      {turn.tools.map(tool => (
                        <li
                          key={tool.id}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground"
                        >
                          {tool.status === 'working' ? (
                            <CircleNotch className="h-3.5 w-3.5 animate-spin" />
                          ) : tool.status === 'completed' ? (
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 text-destructive" />
                          )}
                          {toolCallLabel(tool.name, tool.args)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {turn.content ? (
                    renderMarkdown(sanitizeStreamingContent(turn.content), {
                      id: 'streaming',
                      showFollowups: false,
                    })
                  ) : (
                    <div
                      className="flex items-center gap-1 py-1"
                      aria-label="Benny is thinking"
                    >
                      <span className="benny-typing-dot" />
                      <span className="benny-typing-dot" />
                      <span className="benny-typing-dot" />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <footer
          className={cn(
            'shrink-0 border-t border-border/60 px-3 py-3',
            variant === 'page' && 'px-4',
          )}
        >
          <div
            className={cn(
              'flex flex-col gap-2',
              variant === 'page' && 'mx-auto w-full max-w-3xl',
            )}
          >
            {notice && (
              <p role="status" className="px-1 text-xs text-muted-foreground">
                {notice}
              </p>
            )}
            {readOnly ? (
              <p className="px-1 text-xs text-muted-foreground">
                You’re previewing this portal, so you can’t chat with Benny
                here.
              </p>
            ) : (
              <form
                onSubmit={event => {
                  event.preventDefault()
                  send(draft)
                }}
                className="benny-composer-ring"
              >
                <div className="flex items-end gap-2 rounded-[13px] bg-background p-1.5 pl-3.5">
                  <textarea
                    ref={inputRef}
                    aria-label="Message Benny"
                    placeholder="Ask Benny anything…"
                    rows={1}
                    value={draft}
                    maxLength={8000}
                    onChange={event => setDraft(event.target.value)}
                    onKeyDown={event => {
                      if (
                        event.key === 'Enter' &&
                        !event.shiftKey &&
                        !event.nativeEvent.isComposing
                      ) {
                        event.preventDefault()
                        send(draft)
                      }
                    }}
                    className="max-h-32 min-h-[36px] min-w-0 flex-1 resize-none bg-transparent py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  />
                  <button
                    type="submit"
                    aria-label="Send message"
                    disabled={!draft.trim() || busy}
                    className="flex h-8 w-8 flex-none items-center justify-center rounded-[10px] bg-gradient-to-br from-primary to-cyan-500 text-primary-foreground shadow-[0_4px_12px_-4px_hsl(var(--primary)/0.6)] transition-transform hover:scale-105 disabled:scale-100 disabled:from-muted disabled:to-muted disabled:text-muted-foreground disabled:shadow-none motion-reduce:transition-none"
                  >
                    {busy ? (
                      <CircleNotch className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowUp className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </footer>
      </div>
    </AssistantCardPathContext>
  )
}
